import { VFC } from 'react';

import { MatchIconAlert, MatchIconPositive } from 'dashboard/components/ReportResult/MatchIcons';
import ReportResultLayout from 'dashboard/components/ReportResult/ReportResultLayout';
import TableLayout, { TableTD, TableTH, TableTR } from 'dashboard/layouts/TableLayout';
import {
  ClassificationInformation,
  ReportBusinessClassification,
} from 'dashboard/models/report/business-classification';
import Card from 'design-system/components/Card';
import Sticker from 'design-system/components/Sticker';

const redactedSticker = <Sticker>Redacted</Sticker>;

interface ClassificationInformationTableProps {
  title: string;
  information: ClassificationInformation[];
  isRedacted: boolean;
}

const ClassificationInformationTable = ({
  title,
  information,
  isRedacted,
}: ClassificationInformationTableProps) => {
  const idPrefix = title.replace(/\s+/g, '-').toLowerCase();

  return (
    <>
      <Card.Title>{title}</Card.Title>
      <TableLayout
        isLoading={false}
        showHeader={true}
        isEmpty={false}
        nullState={null}
        headerTR={
          <>
            <TableTH style={{ width: '20%' }}>Description</TableTH>
            <TableTH>Code</TableTH>
            <TableTH>Confidence</TableTH>
            <TableTH style={{ width: '60%' }}>Analysis</TableTH>
          </>
        }
      >
        {isRedacted ? (
          <TableTR id={`${idPrefix}-table__row--description`} key={`${idPrefix}-redacted`}>
            <TableTD>{redactedSticker}</TableTD>
            <TableTD>{redactedSticker}</TableTD>
            <TableTD>{redactedSticker}</TableTD>
            <TableTD>{redactedSticker}</TableTD>
          </TableTR>
        ) : (
          information.map((info, index) => {
            return (
              <TableTR id={`${idPrefix}-table__row--description`} key={`${idPrefix}-${index + 1}`}>
                <TableTD>{info['description']}</TableTD>
                <TableTD>{info['code']}</TableTD>
                <TableTD>{info['confidence']}</TableTD>
                <TableTD>{info['analysis']}</TableTD>
              </TableTR>
            );
          })
        )}
      </TableLayout>
    </>
  );
};

export interface BusinessClassificationProps {
  report: ReportBusinessClassification;
  isRedacted: boolean;
  multiCardFormat: boolean;
}

const BusinessClassification: VFC<BusinessClassificationProps> = ({
  report,
  isRedacted,
  multiCardFormat = false,
}: BusinessClassificationProps) => {
  const naicsInfo = report.attributes.result?.['naics-information'] ?? [];
  const mccInfo = report.attributes.result?.['mcc-information'] ?? [];
  const keywords = report.attributes.result?.['keywords'];
  const isHighRisk = report.attributes.result?.['is-high-risk'];

  return (
    <ReportResultLayout multiCardFormat={multiCardFormat}>
      <>
        <Card.Title>Risk Assessment</Card.Title>
        <TableLayout
          isLoading={false}
          showHeader={true}
          isEmpty={false}
          nullState={null}
          headerTR={
            <>
              <TableTH style={{ width: '20%' }}>Category</TableTH>
              <TableTH style={{ width: '80%' }}>Assessment</TableTH>
            </>
          }
        >
          <TableTR
            data-test='risk-assessment-table__row--is-high-risk'
            id='risk-assessment-table__row--is-high-risk'
            key='is-high-risk'
          >
            <TableTD>Industry</TableTD>
            <TableTD>
              <>{isHighRisk ? <MatchIconAlert label='Yes' /> : <MatchIconPositive label='No' />}</>
            </TableTD>
          </TableTR>
          <TableTR
            data-test='risk-assessment-table__row--keywords'
            id='risk-assessment-table__row--keywords'
            key='keywords'
          >
            <TableTD>Keywords</TableTD>
            <TableTD>
              {keywords?.map((k) => k.charAt(0).toUpperCase() + k.slice(1)).join(', ')}
            </TableTD>
          </TableTR>
        </TableLayout>
      </>
      <ClassificationInformationTable
        title='Predicted NAICS Codes'
        information={naicsInfo as ClassificationInformation[]}
        isRedacted={isRedacted}
      />
      <ClassificationInformationTable
        title='Predicted Merchant Category Codes'
        information={mccInfo as ClassificationInformation[]}
        isRedacted={isRedacted}
      />
    </ReportResultLayout>
  );
};

export default BusinessClassification;
